import React, { useEffect, useState } from 'react';
import {
  Box, Flex, IconButton, Input, InputGroup, InputLeftElement, Text,
} from '@chakra-ui/react';
import { LuEllipsis, LuSearch, LuX } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import PhoneSheet from '../phone/PhoneSheet';
import { SEARCH_THRESHOLD, filterSessions, groupSessions } from './sessionList';

/**
 * The phone's conversation history: a full-height sheet with the
 * conversations grouped by recency, a search from SEARCH_THRESHOLD
 * conversations on (never focused on open), and ⋯ on each row for Delete,
 * which the page confirms. Tapping a row opens that conversation.
 */
export default function PhoneChatHistory({
  isOpen, onClose, sessions, activeId, onSelect, onDelete,
}) {
  const { t } = useTranslation('chat');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (isOpen) setSearch('');
  }, [isOpen]);

  const groups = groupSessions(filterSessions(sessions, search));

  return (
    <PhoneSheet
      id="chat-history"
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={t('phone.historyTitle')}
      headerLeft={(
        <IconButton
          icon={<LuX size={22} />}
          variant="ghost"
          borderRadius="full"
          color="redesign.text"
          minW="44px"
          minH="44px"
          aria-label={t('assistant.close')}
          onClick={onClose}
        />
      )}
      headerRight={null}
    >
      {sessions.length >= SEARCH_THRESHOLD && (
        <Box px="16px" pt="4px" pb="4px">
          <InputGroup>
            <InputLeftElement h="44px" pointerEvents="none" color="phone.muted">
              <LuSearch size={18} />
            </InputLeftElement>
            <Input
              type="search"
              enterKeyHint="search"
              autoComplete="off"
              autoCorrect="off"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('history.searchPlaceholder')}
              h="44px"
              fontSize="16px"
              bg="redesign.bg.card"
              borderColor="redesign.border"
              borderRadius="12px"
            />
          </InputGroup>
        </Box>
      )}

      {groups.length === 0 ? (
        <Text textStyle="phoneBody" color="phone.muted" textAlign="center" px="16px" py="32px">
          {t('history.empty')}
        </Text>
      ) : (
        groups.map((group) => (
          <Box key={group.key}>
            {/* Lined up with the row text, not the card edge. */}
            <Text fontSize="12px" lineHeight="16px" fontWeight="600" textTransform="uppercase" letterSpacing=".05em" color="phone.muted" px="32px" pt="18px" pb="8px">
              {t(`history.${group.key}`)}
            </Text>
            <Box
              mx="16px"
              bg="redesign.bg.card"
              borderWidth="1px"
              borderColor="redesign.border"
              borderRadius="14px"
              overflow="hidden"
            >
              {group.items.map((s, i) => (
                <React.Fragment key={s.id}>
                  {i > 0 && <Box h="1px" ml="16px" bg="redesign.border" />}
                  <Flex align="center" minH="56px">
                    <Box
                      as="button"
                      type="button"
                      flex="1"
                      minW={0}
                      minH="56px"
                      pl="16px"
                      textAlign="left"
                      onClick={() => onSelect(s.id)}
                      _active={{ bg: 'redesign.bg.bubble' }}
                      sx={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Text
                        textStyle="phoneBody"
                        fontWeight={s.id === activeId ? '600' : '400'}
                        color="redesign.heading"
                        noOfLines={1}
                      >
                        {s.title}
                      </Text>
                    </Box>
                    <IconButton
                      icon={<LuEllipsis size={20} />}
                      variant="ghost"
                      color="phone.muted"
                      minW="44px"
                      minH="44px"
                      mr="4px"
                      borderRadius="full"
                      aria-label={t('history.deleteAria')}
                      onClick={() => onDelete(s.id)}
                    />
                    </Flex>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        ))
      )}
    </PhoneSheet>
  );
}
